type ResultadoBusqueda = {
  numero: number;
  documentId: string;
  tipo: string;
  titulo: string;
  url: string;
  fecha?: string | null;
  fragmento: string;
};

type FuenteBusqueda = ResultadoBusqueda;

type RespuestaResultados = {
  estado: 'con_resultados' | 'sin_resultados';
  resultados: ResultadoBusqueda[];
};

type RespuestaGenerada = {
  estado: 'con_respuesta' | 'sin_resultados' | 'sin_respuesta';
  respuesta?: string | null;
  fuentes: FuenteBusqueda[];
};

const MAX_FRAGMENTO = 300;
const SELECTOR_EDICION = 'input, textarea, select, [contenteditable]';

export function clicFueraDelRectangulo(
  rect: Pick<DOMRect, 'left' | 'right' | 'top' | 'bottom'>,
  x: number,
  y: number,
): boolean {
  return x < rect.left || x > rect.right || y < rect.top || y > rect.bottom;
}

function anexarTextoConCitas(contenedor: Node, texto: string, documento: Document): void {
  const patron = /\[(\d+)\]/g;
  let inicio = 0;
  let coincidencia: RegExpExecArray | null;

  while ((coincidencia = patron.exec(texto)) !== null) {
    contenedor.appendChild(documento.createTextNode(texto.slice(inicio, coincidencia.index)));
    const enlace = documento.createElement('a');
    enlace.href = `#fuente-${coincidencia[1]}`;
    enlace.textContent = coincidencia[0];
    contenedor.appendChild(enlace);
    inicio = patron.lastIndex;
  }

  contenedor.appendChild(documento.createTextNode(texto.slice(inicio)));
}

/** Convierte texto plano en nodos seguros. Se exporta para probar la frontera XSS. */
export function crearNodosRespuesta(texto: string, documento: Document = document): DocumentFragment {
  const fragmento = documento.createDocumentFragment();
  let lista: HTMLUListElement | null = null;

  texto.split(/\r?\n/).forEach((linea) => {
    const limpia = linea.trim();
    if (!limpia) {
      lista = null;
      return;
    }

    if (limpia.startsWith('- ')) {
      if (!lista) {
        lista = documento.createElement('ul');
        fragmento.appendChild(lista);
      }
      const elemento = documento.createElement('li');
      anexarTextoConCitas(elemento, limpia.slice(2), documento);
      lista.appendChild(elemento);
      return;
    }

    lista = null;
    const parrafo = documento.createElement('p');
    anexarTextoConCitas(parrafo, limpia, documento);
    fragmento.appendChild(parrafo);
  });

  return fragmento;
}

function vaciar(elemento: Element | null): void {
  elemento?.replaceChildren();
}

function urlSegura(url: string): string {
  if (url.startsWith('/') && !url.startsWith('//')) return url;
  try {
    const absoluta = new URL(url);
    return absoluta.protocol === 'http:' || absoluta.protocol === 'https:' ? absoluta.href : '#';
  } catch {
    return '#';
  }
}

function pintarSinResultados(contenedor: HTMLElement | null): void {
  if (!contenedor) return;
  contenedor.replaceChildren();
  const texto = document.createElement('p');
  texto.textContent = 'No hemos encontrado contenidos relacionados. Puede consultar ';
  const tramites = document.createElement('a');
  tramites.href = '/tramites';
  tramites.textContent = 'los trámites';
  texto.append(tramites, document.createTextNode(' o '));
  const contacto = document.createElement('a');
  contacto.href = '/contacto';
  contacto.textContent = 'contactar con nosotros';
  texto.append(contacto, document.createTextNode('.'));
  contenedor.appendChild(texto);
  contenedor.hidden = false;
}

function pintarResultados(lista: HTMLOListElement | null, resultados: ResultadoBusqueda[]): void {
  if (!lista) return;
  lista.replaceChildren();

  resultados.forEach((resultado) => {
    const item = document.createElement('li');
    item.className = 'buscador__resultado';
    const titulo = document.createElement('h3');
    const enlace = document.createElement('a');
    enlace.href = urlSegura(resultado.url);
    enlace.textContent = resultado.titulo;
    titulo.appendChild(enlace);

    const meta = document.createElement('p');
    meta.className = 'buscador__meta';
    const tipo = document.createElement('span');
    tipo.textContent = resultado.tipo;
    meta.appendChild(tipo);
    if (resultado.fecha) {
      const fecha = document.createElement('time');
      fecha.dateTime = resultado.fecha;
      fecha.textContent = resultado.fecha;
      meta.appendChild(fecha);
    }

    const fragmento = document.createElement('p');
    fragmento.textContent = resultado.fragmento.slice(0, MAX_FRAGMENTO);
    item.append(titulo, meta, fragmento);
    lista.appendChild(item);
  });
}

function pintarFuentes(lista: HTMLOListElement | null, fuentes: FuenteBusqueda[]): void {
  if (!lista) return;
  lista.replaceChildren();

  fuentes.forEach((fuente) => {
    const item = document.createElement('li');
    item.id = `fuente-${fuente.numero}`;
    item.value = fuente.numero;
    const enlace = document.createElement('a');
    enlace.href = urlSegura(fuente.url);
    enlace.textContent = fuente.titulo;
    const detalle = document.createElement('span');
    detalle.textContent = ` — ${fuente.tipo}${fuente.fecha ? ` · ${fuente.fecha}` : ''}`;
    // Número visible igual al de las citas del texto ([1], [3]…): la numeración de la lista
    // puede saltar números y el marcador nativo del <ol> no se muestra en el tema.
    const numero = document.createElement('span');
    numero.className = 'buscador__fuente-numero';
    numero.textContent = `[${fuente.numero}] `;
    item.append(numero, enlace, detalle);
    lista.appendChild(item);
  });
}

function mensajeError(respuesta: Response): string {
  if (respuesta.status === 429) {
    const espera = Number.parseInt(respuesta.headers.get('Retry-After') ?? '', 10);
    const segundos = Number.isFinite(espera) && espera > 0 ? espera : 60;
    return `Demasiadas búsquedas. Inténtelo en ${segundos} segundos`;
  }
  if (respuesta.status === 400) return 'La consulta no es válida';
  return 'El buscador no está disponible en este momento';
}

export function initBuscador(root?: HTMLElement): void {
  if (!root || root.dataset.buscadorInicializado === 'true') return;
  root.dataset.buscadorInicializado = 'true';

  const dialogo = root instanceof HTMLDialogElement ? root : null;
  const formulario = root.querySelector<HTMLFormElement>('[data-buscador-formulario]');
  const campo = root.querySelector<HTMLInputElement>('[data-buscador-consulta]');
  const estado = root.querySelector<HTMLElement>('[data-buscador-estado]');
  const listaResultados = root.querySelector<HTMLOListElement>('[data-buscador-resultados]');
  const sinResultados = root.querySelector<HTMLElement>('[data-buscador-sin-resultados]');
  const bloqueRespuesta = root.querySelector<HTMLElement>('[data-buscador-respuesta]');
  const textoRespuesta = root.querySelector<HTMLElement>('[data-buscador-respuesta-texto]');
  const listaFuentes = root.querySelector<HTMLOListElement>('[data-buscador-fuentes]');
  const enlacePagina = root.querySelector<HTMLAnchorElement>('[data-buscador-pagina]');
  let peticiones: AbortController | null = null;
  let botonOrigen: HTMLElement | null = null;

  const actualizarEstado = (texto: string) => {
    if (estado) estado.textContent = texto;
  };

  const limpiarRespuesta = () => {
    if (bloqueRespuesta) bloqueRespuesta.hidden = true;
    vaciar(textoRespuesta);
    vaciar(listaFuentes);
  };

  const tratarRespuestaHttp = async <T>(respuesta: Response): Promise<T> => {
    if (!respuesta.ok) throw new Error(mensajeError(respuesta));
    return respuesta.json() as Promise<T>;
  };

  const obtenerRespuesta = async (consulta: string, signal: AbortSignal): Promise<RespuestaGenerada> => {
    const respuesta = await fetch('/front/busquedas/respuesta', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ q: consulta }),
      signal,
    });
    return tratarRespuestaHttp<RespuestaGenerada>(respuesta);
  };

  const pintarRespuesta = (datos: RespuestaGenerada): void => {
    if (datos.estado === 'con_respuesta' && datos.respuesta) {
      textoRespuesta?.appendChild(crearNodosRespuesta(datos.respuesta));
      pintarFuentes(listaFuentes, datos.fuentes ?? []);
      if (bloqueRespuesta) bloqueRespuesta.hidden = false;
      actualizarEstado('Respuesta generada');
    } else if (datos.estado === 'sin_respuesta') {
      actualizarEstado('No hemos encontrado una respuesta en el portal');
    } else if (datos.estado === 'con_respuesta') {
      actualizarEstado('No hemos encontrado una respuesta en el portal');
    } else if (datos.estado === 'sin_resultados') {
      pintarSinResultados(sinResultados);
      actualizarEstado('No hemos encontrado contenidos relacionados');
    }
  };

  const buscar = async (consulta: string, soloRespuesta = false): Promise<void> => {
    peticiones?.abort();
    peticiones = new AbortController();
    const { signal } = peticiones;
    actualizarEstado('Buscando…');
    limpiarRespuesta();
    if (!soloRespuesta) {
      vaciar(listaResultados);
      if (sinResultados) sinResultados.hidden = true;
    }
    if (enlacePagina) enlacePagina.href = `/buscar?q=${encodeURIComponent(consulta)}`;

    const promesaResultados = soloRespuesta ? null : fetch(
      `/front/busquedas/resultados?q=${encodeURIComponent(consulta)}`,
      { signal },
    ).then(tratarRespuestaHttp<RespuestaResultados>);
    const promesaRespuesta: Promise<{ datos?: RespuestaGenerada; error?: unknown }> = obtenerRespuesta(consulta, signal).then(
      (datos) => ({ datos }),
      (error: unknown) => ({ error }),
    );

    try {
      if (promesaResultados) {
        const datos = await promesaResultados;
        if (datos.estado === 'sin_resultados' || datos.resultados.length === 0) {
          pintarSinResultados(sinResultados);
          actualizarEstado('No hemos encontrado contenidos relacionados');
        } else {
          pintarResultados(listaResultados, datos.resultados);
          actualizarEstado(`${datos.resultados.length} resultados`);
        }
      }
      const resultadoRespuesta = await promesaRespuesta;
      if (resultadoRespuesta.error) throw resultadoRespuesta.error;
      if (resultadoRespuesta.datos) pintarRespuesta(resultadoRespuesta.datos);
    } catch (error) {
      if (signal.aborted) return;
      actualizarEstado(error instanceof Error ? error.message : 'El buscador no está disponible en este momento');
    }
  };

  formulario?.addEventListener('submit', (evento) => {
    if (!campo || !formulario.checkValidity()) return;
    evento.preventDefault();
    const consulta = campo.value.trim();
    if (consulta.length < 3 || consulta.length > 300) {
      actualizarEstado('La consulta no es válida');
      campo.focus();
      return;
    }
    void buscar(consulta);
  });
  formulario?.addEventListener('invalid', () => actualizarEstado('La consulta no es válida'), true);

  if (dialogo) {
    const botonesAbrir = Array.from(document.querySelectorAll<HTMLElement>('.search-toggler'));
    const sincronizar = (abierto: boolean) => botonesAbrir.forEach((boton) =>
      boton.setAttribute('aria-expanded', String(abierto)));
    const abrir = (origen?: HTMLElement) => {
      botonOrigen = origen ?? botonesAbrir[0] ?? null;
      if (!dialogo.open) dialogo.showModal();
      sincronizar(true);
      window.setTimeout(() => campo?.focus(), 0);
    };

    botonesAbrir.forEach((boton) => boton.addEventListener('click', () => abrir(boton)));
    root.querySelector<HTMLElement>('[data-buscador-cerrar]')?.addEventListener('click', () => dialogo.close());
    dialogo.addEventListener('click', (evento) => {
      if (evento.target !== dialogo) return;
      const rect = dialogo.getBoundingClientRect();
      if (clicFueraDelRectangulo(rect, evento.clientX, evento.clientY)) dialogo.close();
    });
    dialogo.addEventListener('close', () => {
      peticiones?.abort();
      sincronizar(false);
      botonOrigen?.focus();
      botonOrigen = null;
    });
    document.addEventListener('keydown', (evento) => {
      if (evento.key.toLowerCase() !== 'k' || (!evento.ctrlKey && !evento.metaKey)) return;
      const activo = document.activeElement;
      if (activo instanceof Element && activo.matches(SELECTOR_EDICION)) return;
      evento.preventDefault();
      abrir();
    });
  }

  const consultaInicial = root.dataset.buscadorConsultaInicial?.trim();
  if (!dialogo && consultaInicial && consultaInicial.length >= 3 && consultaInicial.length <= 300) {
    void buscar(consultaInicial, true);
  }
}
